import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  UploadCloud,
  Search,
  Image as ImageIcon,
  FileText,
  Film,
  Trash2,
  X,
  Copy,
  Check,
  Loader2,
  Filter,
  ChevronDown,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import api, { mediaUrl } from '../api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const KIND_META = {
  image: { label: 'Image', icon: ImageIcon, color: '#5DCAB5' },
  pdf: { label: 'PDF', icon: FileText, color: '#E5614C' },
  video: { label: 'Video', icon: Film, color: '#FCD968' },
  other: { label: 'File', icon: FileText, color: '#9ca3af' },
};

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/svg+xml,application/pdf,video/mp4';
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

export function formatBytes(n) {
  if (n == null || n === 0) return '–';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(s) {
  try {
    const d = new Date(s);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return s;
  }
}

// ---------------------------------------------------------------------------
// Upload dropzone — drag/drop + click, multi-file, per-file progress
// ---------------------------------------------------------------------------
export function UploadDropzone({ onUploaded, compact = false }) {
  const [dragging, setDragging] = useState(false);
  const [queue, setQueue] = useState([]); // [{name, status, progress, error}]
  const inputRef = useRef(null);

  const handleFiles = useCallback(async (files) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    const initial = list.map((f) => ({
      id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: f.name,
      size: f.size,
      status: 'pending',
      progress: 0,
      error: null,
    }));
    setQueue((q) => [...initial, ...q]);

    for (let i = 0; i < list.length; i++) {
      const file = list[i];
      const id = initial[i].id;
      if (file.size > MAX_UPLOAD_BYTES) {
        setQueue((q) => q.map((item) => item.id === id ? { ...item, status: 'error', error: 'Over 5 MB limit' } : item));
        continue;
      }
      setQueue((q) => q.map((item) => item.id === id ? { ...item, status: 'uploading' } : item));
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/media/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (!e.total) return;
            const pct = Math.round((e.loaded / e.total) * 100);
            setQueue((q) => q.map((item) => item.id === id ? { ...item, progress: pct } : item));
          },
        });
        setQueue((q) => q.map((item) => item.id === id ? { ...item, status: 'done', progress: 100 } : item));
        onUploaded?.(data);
      } catch (err) {
        const msg = err.response?.data?.detail || 'Upload failed';
        setQueue((q) => q.map((item) => item.id === id ? { ...item, status: 'error', error: msg } : item));
      }
    }
    // Auto-clear completed entries after a few seconds
    setTimeout(() => {
      setQueue((q) => q.filter((item) => item.status !== 'done'));
    }, 2500);
  }, [onUploaded]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-3" data-testid="media-upload-zone">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative rounded-2xl border-2 border-dashed cursor-pointer transition-[border-color,background-color,transform] duration-200 ${
          dragging
            ? 'border-[#E5614C] bg-[#E5614C]/10 scale-[1.01]'
            : 'border-white/15 hover:border-white/30 hover:bg-white/[0.02]'
        } ${compact ? 'p-5' : 'p-8'}`}
      >
        <div className="flex flex-col items-center text-center gap-2">
          <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${dragging ? 'bg-[#E5614C] text-white' : 'bg-white/5 text-white/55'}`}>
            <UploadCloud className="h-6 w-6" />
          </div>
          <div className="text-white text-[14px] font-semibold">
            {dragging ? 'Drop to upload' : 'Drag & drop or click to upload'}
          </div>
          {!compact && (
            <div className="text-[12px] text-white/45 max-w-md">
              Images (JPG, PNG, WebP, GIF, SVG), PDFs, or MP4 video — up to 5 MB each.
              Images are auto-compressed to ~50–300 KB for fast loads.
            </div>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
          data-testid="media-upload-input"
        />
      </div>

      {queue.length > 0 && (
        <div className="space-y-1.5">
          {queue.map((item) => (
            <div key={item.id} className="flex items-center gap-3 bg-[#16131a] border border-white/[0.06] rounded-lg px-3 py-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[12px] font-semibold text-white truncate">{item.name}</div>
                  <div className="text-[11px] text-white/50 shrink-0">
                    {item.status === 'done' && <span className="text-[#5DCAB5] inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Done</span>}
                    {item.status === 'error' && <span className="text-red-400">{item.error}</span>}
                    {item.status === 'uploading' && <span>{item.progress}%</span>}
                    {item.status === 'pending' && <span>Queued…</span>}
                  </div>
                </div>
                <div className="mt-1.5 h-1 rounded-full bg-white/[0.06] overflow-hidden">
                  <div
                    className={`h-full transition-[width] duration-300 ${item.status === 'error' ? 'bg-red-500/70' : 'bg-[#E5614C]'}`}
                    style={{ width: `${item.status === 'done' ? 100 : item.progress}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Media item thumbnail — used by both library and picker
// ---------------------------------------------------------------------------
export function MediaThumb({ item, selected, selectable, onToggle, onClick }) {
  const meta = KIND_META[item.kind] || KIND_META.other;
  const Icon = meta.icon;
  const isImage = item.kind === 'image';
  return (
    <div
      data-testid={`media-item-${item.id}`}
      onClick={onClick}
      className={`group relative rounded-xl overflow-hidden border cursor-pointer transition-[border-color,transform,box-shadow] duration-200 ${
        selected
          ? 'border-[#E5614C] shadow-[0_0_0_3px_rgba(229,97,76,0.35)]'
          : 'border-white/[0.08] hover:border-white/25 hover:-translate-y-0.5'
      } bg-[#16131a]`}
    >
      <div className="aspect-square bg-[#0f0d12] flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img
            src={mediaUrl(item.url)}
            alt={item.filename}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-1.5 text-white/60">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${meta.color}22`, color: meta.color }}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold">{meta.label}</div>
          </div>
        )}
      </div>
      <div className="px-2.5 py-2 border-t border-white/[0.04]">
        <div className="text-[11.5px] font-semibold text-white truncate" title={item.filename}>{item.filename}</div>
        <div className="text-[10px] text-white/40 mt-0.5 flex items-center justify-between gap-2">
          <span>{formatBytes(item.size)}</span>
          {item.width && item.height && <span className="truncate">{item.width}×{item.height}</span>}
        </div>
      </div>
      {selectable && (
        <div
          className={`absolute top-2 left-2 ${selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
          onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
        >
          <div className={`h-6 w-6 rounded-md flex items-center justify-center backdrop-blur-sm border ${selected ? 'bg-[#E5614C] border-[#E5614C] text-white' : 'bg-black/50 border-white/30 text-transparent'}`}>
            <Check className="h-3.5 w-3.5" />
          </div>
        </div>
      )}
      {selected && !selectable && (
        <div className="absolute inset-0 ring-2 ring-[#E5614C] rounded-xl pointer-events-none" />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar — search, kind filter, sort
// ---------------------------------------------------------------------------
export function MediaToolbar({ q, setQ, kind, setKind, sort, setSort, kindCounts }) {
  const kindOptions = [
    { id: '', label: 'All', icon: Filter },
    { id: 'image', label: 'Images', icon: ImageIcon },
    { id: 'pdf', label: 'PDFs', icon: FileText },
    { id: 'video', label: 'Videos', icon: Film },
  ];
  return (
    <div className="flex flex-wrap items-center gap-3 mb-5">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search filenames…"
          className="h-10 pl-9 rounded-lg bg-[#0f0d12] border-white/10 text-white text-[13px] placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
          data-testid="media-search"
        />
      </div>
      <div className="flex gap-1 bg-[#0f0d12] border border-white/10 rounded-lg p-1">
        {kindOptions.map((opt) => {
          const Icon = opt.icon;
          const count = opt.id ? kindCounts?.[opt.id] : Object.values(kindCounts || {}).reduce((a, b) => a + b, 0);
          return (
            <button
              key={opt.id}
              onClick={() => setKind(opt.id)}
              className={`px-2.5 h-8 rounded-md inline-flex items-center gap-1.5 text-[12px] font-semibold transition-colors ${
                kind === opt.id ? 'bg-[#E5614C] text-white' : 'text-white/55 hover:text-white'
              }`}
              data-testid={`media-kind-${opt.id || 'all'}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {opt.label}
              {count != null && <span className={`text-[10px] ${kind === opt.id ? 'text-white/80' : 'text-white/35'}`}>{count}</span>}
            </button>
          );
        })}
      </div>
      <div className="relative">
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="h-10 pl-3 pr-9 rounded-lg bg-[#0f0d12] border border-white/10 text-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#E5614C]/40 appearance-none"
          data-testid="media-sort"
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">Name (A–Z)</option>
          <option value="size_desc">Largest first</option>
          <option value="size_asc">Smallest first</option>
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40 pointer-events-none" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer
// ---------------------------------------------------------------------------
export function MediaDetailDrawer({ item, onClose, onDelete }) {
  const [copied, setCopied] = useState(false);
  const [deleting, setDeleting] = useState(false);
  if (!item) return null;
  const fullUrl = mediaUrl(item.url);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy URL');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${item.filename}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/media/${item.id}`);
      toast.success('Deleted');
      onDelete?.(item.id);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const meta = KIND_META[item.kind] || KIND_META.other;
  const isImage = item.kind === 'image';

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="media-detail-drawer">
      <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <aside className="w-full max-w-[440px] bg-[#16131a] border-l border-white/[0.06] text-white overflow-y-auto">
        <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#5DCAB5]">Asset Details</div>
          <button onClick={onClose} className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid="media-detail-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-5">
          <div className="rounded-2xl bg-[#0f0d12] border border-white/[0.06] overflow-hidden flex items-center justify-center min-h-[220px]">
            {isImage ? (
              <img src={fullUrl} alt={item.filename} className="w-full h-auto max-h-[420px] object-contain" />
            ) : item.kind === 'video' ? (
              <video src={fullUrl} controls className="w-full max-h-[420px]" />
            ) : (
              <a href={fullUrl} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-3 py-12 text-white/75 hover:text-white">
                <div className="h-14 w-14 rounded-2xl flex items-center justify-center" style={{ background: `${meta.color}22`, color: meta.color }}>
                  <meta.icon className="h-7 w-7" />
                </div>
                <span className="text-[13px] font-semibold underline-offset-4 hover:underline">Open {meta.label}</span>
              </a>
            )}
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-semibold mb-1">Filename</div>
            <div className="text-[14px] font-semibold break-all">{item.filename}</div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-[12px]">
            <Stat label="Type" value={meta.label} />
            <Stat label="MIME" value={item.mime} mono />
            <Stat label="Size" value={formatBytes(item.size)} />
            <Stat label="Original" value={formatBytes(item.original_size)} />
            {item.width && item.height && <Stat label="Dimensions" value={`${item.width} × ${item.height}`} />}
            <Stat label="Uploaded" value={formatDate(item.uploaded_at)} />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-semibold mb-1">Public URL</div>
            <div className="flex gap-2">
              <Input value={fullUrl} readOnly className="h-10 rounded-lg bg-[#0f0d12] border-white/10 text-white text-[11.5px]" />
              <Button onClick={copy} className="h-10 rounded-lg bg-white/5 hover:bg-white/10 text-white border border-white/10 px-3" data-testid="media-copy-url">
                {copied ? <Check className="h-4 w-4 text-[#5DCAB5]" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <Button
            onClick={handleDelete}
            disabled={deleting}
            className="w-full h-11 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 font-semibold"
            data-testid="media-delete-btn"
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
            Delete asset
          </Button>
        </div>
      </aside>
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="bg-[#0f0d12] border border-white/[0.06] rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">{label}</div>
      <div className={`text-[13px] text-white mt-0.5 ${mono ? 'font-mono text-[11.5px] break-all' : 'font-semibold'}`}>{value || '–'}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Media Library page
// ---------------------------------------------------------------------------
export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [sort, setSort] = useState('newest');
  const [selected, setSelected] = useState(() => new Set()); // multi-select
  const [active, setActive] = useState(null); // detail drawer item
  const [stats, setStats] = useState({ total: 0, by_kind: {}, total_bytes: 0 });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const debounceRef = useRef(null);

  const load = useCallback(async (params = {}) => {
    try {
      const { data } = await api.get('/media', { params });
      setItems(data);
    } catch (err) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshStats = useCallback(async () => {
    try {
      const { data } = await api.get('/media/stats');
      setStats(data);
    } catch { /* non-fatal */ }
  }, []);

  // initial load
  useEffect(() => {
    load({ sort });
    refreshStats();
     
  }, []);

  // re-fetch on filter change (debounced search)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load({ q: q || undefined, kind: kind || undefined, sort });
    }, q ? 300 : 0);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
     
  }, [q, kind, sort]);

  const onUploaded = (item) => {
    setItems((arr) => [item, ...arr.filter((x) => x.id !== item.id)]);
    refreshStats();
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} item${selected.size === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post('/media/bulk-delete', { ids });
      toast.success(`Deleted ${data.deleted} item${data.deleted === 1 ? '' : 's'}`);
      setItems((arr) => arr.filter((x) => !selected.has(x.id)));
      clearSelection();
      refreshStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const onDeleteItem = (id) => {
    setItems((arr) => arr.filter((x) => x.id !== id));
    setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
    refreshStats();
  };

  const kindCounts = stats.by_kind || {};
  const totalBytes = stats.total_bytes || 0;

  const allSelectedOnPage = useMemo(
    () => items.length > 0 && items.every((it) => selected.has(it.id)),
    [items, selected]
  );

  const toggleSelectAll = () => {
    if (allSelectedOnPage) {
      clearSelection();
    } else {
      setSelected(new Set(items.map((it) => it.id)));
    }
  };

  return (
    <div className="space-y-7" data-testid="admin-media-library">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#5DCAB5] font-semibold">CMS</div>
          <h1 className="font-serif-display text-[32px] font-bold mt-1">Media Library</h1>
          <p className="text-[13px] text-white/55 mt-1">
            All your photos, PDFs, and videos in one place. Images are auto-compressed to ~50–300 KB.
          </p>
        </div>
        <div className="flex items-center gap-6 text-right">
          <Stat2 label="Total" value={stats.total || 0} />
          <Stat2 label="Storage" value={formatBytes(totalBytes)} />
        </div>
      </div>

      {/* Upload zone */}
      <UploadDropzone onUploaded={onUploaded} />

      {/* Toolbar */}
      <MediaToolbar
        q={q} setQ={setQ}
        kind={kind} setKind={setKind}
        sort={sort} setSort={setSort}
        kindCounts={kindCounts}
      />

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 bg-[#E5614C]/15 border border-[#E5614C]/30 rounded-xl px-4 py-3" data-testid="media-bulk-bar">
          <div className="flex items-center gap-3">
            <Checkbox checked={allSelectedOnPage} onCheckedChange={toggleSelectAll} className="border-white/30" />
            <div className="text-[13px] text-white">
              <span className="font-semibold">{selected.size}</span> selected
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={clearSelection} variant="ghost" className="h-9 rounded-lg text-white/70 hover:bg-white/10 hover:text-white text-[13px] font-semibold">
              Clear
            </Button>
            <Button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="h-9 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/30 font-semibold text-[13px]"
              data-testid="media-bulk-delete"
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-xl bg-white/[0.04] animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-[#16131a] border border-dashed border-white/[0.1] p-14 text-center" data-testid="media-empty">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-white/5 text-white/55 flex items-center justify-center">
            <ImageIcon className="h-6 w-6" />
          </div>
          <div className="mt-4 font-serif-display text-[22px] font-bold">No media yet</div>
          <div className="mt-1 text-[13px] text-white/55">Drag a file onto the zone above to get started.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-testid="media-grid">
          {items.map((it) => (
            <MediaThumb
              key={it.id}
              item={it}
              selected={selected.has(it.id)}
              selectable
              onToggle={() => toggleSelect(it.id)}
              onClick={() => setActive(it)}
            />
          ))}
        </div>
      )}

      <MediaDetailDrawer
        item={active}
        onClose={() => setActive(null)}
        onDelete={onDeleteItem}
      />
    </div>
  );
}

function Stat2({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">{label}</div>
      <div className="text-[20px] font-bold font-serif-display text-white mt-0.5">{value}</div>
    </div>
  );
}
